
  import { defineComponent, PropType } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import textMixin from '@/common/mixins/text';
  import Tooltip from '@/common/ui/Tooltip.vue';

  import {
    REGION_DASHBOARDS_VES_ACTIONS_NAME,
    REGION_DASHBOARDS_VES_GETTERS_NAME,
  } from '../../../store/regionDashboards/name';
  import {
    TBaseRegionInfo,
    TDashboardRegionFilter,
    TDashboardRegionFilterPayload,
  } from '../../../types/regionDashboards';

  const BASE_INFO_TOOLTIP = 'Информация по данным субъекта';

  const baseRegionInfoLabels: Record<keyof Omit<TBaseRegionInfo, 'genetiveCase'>, string> = {
    administrativeCenter: 'Административный центр субъекта РФ',
    population: 'Численность населения (чел.)',
    totalArea: 'Общая площадь (кв. км.)',
    vrp: 'Валовой региональный продукт',
    amountMo: 'Количество муниципальных образований',
    borders: 'Граничит',
    enterprisesWithForeignCapital: 'Предприятия с иностранным капиталом',
  };

  export default defineComponent({
    name: 'RegionDashboardsBaseInfo',
    components: {
      Tooltip,
    },
    mixins: [textMixin],
    props: {
      filter: {
        type: Object as PropType<TDashboardRegionFilter>,
        required: true,
      },
    },
    data(): { baseRegionInfoAbortController: AbortController; isLoading: boolean; baseInfoTooltip: string } {
      return {
        baseRegionInfoAbortController: new AbortController(),
        isLoading: false,
        baseInfoTooltip: BASE_INFO_TOOLTIP,
      };
    },
    computed: {
      ...mapGetters({
        baseRegionInfo: REGION_DASHBOARDS_VES_GETTERS_NAME.getBaseRegionInfo,
      }),
      getBaseRegionInfoLabels() {
        return (Object.entries(baseRegionInfoLabels) as [keyof TBaseRegionInfo, string][]).map(([key, label]) => {
          let value = this.baseRegionInfo?.[key];
          const numbersValues: (keyof TBaseRegionInfo)[] = [
            'population',
            'totalArea',
            'amountMo',
            'enterprisesWithForeignCapital',
          ];

          if (numbersValues.includes(key)) {
            value = this.formatNumber(value, 0);
          } else if (key === 'vrp') {
            value = this.formatNumber(value, 1);
          }

          return {
            label,
            value,
          };
        });
      },
      getPreparedFilter(): TDashboardRegionFilter {
        const year = String(Number(this.filter.year) - 1);

        return {
          year,
          regionRfId: this.filter.regionRfId,
        };
      },
    },
    methods: {
      ...mapActions({
        getBaseRegionInfo: REGION_DASHBOARDS_VES_ACTIONS_NAME.getBaseRegionInfo,
      }),
      getValue(value: string) {
        if (value == null || String(value).trim() === '') return 'Нет данных';
        return value;
      },
    },
    watch: {
      filter: {
        async handler() {
          this.baseRegionInfoAbortController.abort();
          this.baseRegionInfoAbortController = new AbortController();

          const payload: TDashboardRegionFilterPayload = {
            params: this.getPreparedFilter,
            abortController: this.baseRegionInfoAbortController,
          };

          this.isLoading = true;
          await this.getBaseRegionInfo(payload);
          this.isLoading = false;
        },
        immediate: true,
      },
    },
  });
